'use client';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vendorSettlementApi } from '../../services/vendorSettlementApi';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    confirmed: { label: 'Onaylandı', color: 'bg-blue-100 text-blue-700' },
    paid: { label: 'Ödendi', color: 'bg-green-100 text-green-700' },
};

export default function VendorSettlementDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [settlement, setSettlement] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSettlement();
    }, [id]);

    const fetchSettlement = async () => {
        try {
            setLoading(true);
            const data = await vendorSettlementApi.getById(id!);
            setSettlement(data);
        } catch {
            toast.error('Mutabakat yüklenemedi');
            navigate('/vendor/settlements');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);
    const formatDate = (val: any) => {
        if (!val) return '-';
        const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
        return d.toLocaleDateString('tr-TR');
    };

    if (loading || !settlement) {
        return (
            <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const st = STATUS_LABELS[settlement.status] || STATUS_LABELS.confirmed;
    const isProfitShare = settlement.agreementType === 'profit_share';

    return (
        <div className="p-6 max-w-5xl">
            {/* Header */}
            <div className="mb-6">
                <button onClick={() => navigate('/vendor/settlements')} className="text-blue-600 hover:underline text-sm mb-2 flex items-center gap-1">
                    <ArrowLeft size={14} /> Mutabakatlar
                </button>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Mutabakat Detayı</h1>
                        <p className="text-gray-500">{formatDate(settlement.periodStart)} - {formatDate(settlement.periodEnd)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-sm px-3 py-1 rounded-full font-medium ${st.color}`}>{st.label}</span>
                        <span className="text-xs px-2 py-1 rounded bg-gray-100">
                            {isProfitShare ? 'Kâr Ortaklığı' : 'Ciro Komisyonu'} — %{settlement.platformShareRate}
                        </span>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Toplam Ciro</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(settlement.totalRevenue)}</p>
                    <p className="text-xs text-gray-400">{settlement.orderCount} sipariş</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Toplam Giderler</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(settlement.totalExpenses)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Platform Payı</p>
                    <p className="text-xl font-bold text-blue-600">{formatCurrency(settlement.platformShare)}</p>
                    <p className="text-xs text-gray-400">%{settlement.platformShareRate}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <p className="text-xs text-gray-500">Payınız</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(settlement.vendorShare)}</p>
                </div>
            </div>

            {/* Expense Breakdown */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="font-semibold text-lg mb-4">Gider Kırılımı</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                        <p className="text-xs text-gray-500">Kurye Ücretleri</p>
                        <p className="font-semibold">{formatCurrency(settlement.totalCourierFees)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Malzeme Maliyeti</p>
                        <p className="font-semibold">{formatCurrency(settlement.totalMaterialCosts)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">POS Komisyonu</p>
                        <p className="font-semibold">{formatCurrency(settlement.totalPosCommission)}</p>
                    </div>
                    {isProfitShare && (
                        <div>
                            <p className="text-xs text-gray-500">Platform Giderleri</p>
                            <p className="font-semibold">{formatCurrency(settlement.platformExpenses)}</p>
                        </div>
                    )}
                    <div>
                        <p className="text-xs text-gray-500">Net Kâr</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(settlement.netProfit)}</p>
                    </div>
                </div>
            </div>

            {/* Payment Status */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="font-semibold text-lg mb-4">Ödeme Bilgisi</h2>
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <p className="text-xs text-gray-500">Ödenen Tutar</p>
                        <p className="font-semibold text-green-600">{formatCurrency(settlement.paidAmount)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Kalan Tutar</p>
                        <p className="font-semibold text-orange-600">{formatCurrency(settlement.remainingAmount)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-500">Ödeme Tarihi</p>
                        <p className="font-semibold">{settlement.paidAt ? formatDate(settlement.paidAt) : 'Henüz ödenmedi'}</p>
                    </div>
                </div>
                {settlement.paymentNote && (
                    <p className="text-sm text-gray-600 mt-3 border-t pt-3">Not: {settlement.paymentNote}</p>
                )}
            </div>
        </div>
    );
}
